import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, register } from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import { KatActivityList } from './activity-list';
import styles from './activity-list-item.lit.scss';

/**
 * @component {kat-activity-list-item} KatActivityListItem An activity card intended to be slotted into an Activity List.
 * @status Production
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-activity-list-item')
export class KatActivityListItem extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /**
   * The header content of the list-item.
   */
  @property()
  header: string;

  /**
   * A string for displaying the timestamp of the card such as "Last updated: HH:MM:SS" or "Last updated 3 seconds ago"
   */
  @property()
  timestamp: string;

  /**
   * Specifies the type of activity-list-item to be displayed. Defaults to "default".
   * @enum {value} default This is the default activity-list-item variant.
   * @enum {value} warning Signals moderate issues that may not require immediate attention.
   * @enum {value} danger Signals critical issues that likely require immediate attention.
   */
  @property()
  variant?: 'default' | 'warning' | 'danger' = 'default';

  /**
   * The completion status of the task represented by the card.
   * Completed tasks have a simplified UI that hides primary actions.
   * Only applicable for Activity List Items in a "task" variant Activity List.
   *
   * Defaults to "incomplete".
   * @enum {value} incomplete Task has not been started yet.
   * @enum {value} current Task currently being worked on.
   * @enum {value} complete Task is completed. Primary actions can no longer be taken on this task.
   */
  @property()
  state?: 'incomplete' | 'current' | 'complete' = 'incomplete';

  renderPrimaryActionSlot() {
    return this.state === 'complete'
      ? nothing
      : html`<slot name="primary-action"></slot>`;
  }

  renderStateIcon() {
    const parent = this.parentElement as KatActivityList;

    if (parent.tagName !== 'KAT-ACTIVITY-LIST' || parent.variant !== 'task') {
      return nothing;
    }

    let icon;
    if (this.state === 'current') {
      icon = 'radio_button_on';
    } else if (this.state === 'complete') {
      icon = 'check_circle';
    } else {
      icon = 'radio_button_off';
    }

    return html` <kat-icon class="state" name=${icon} size="small"></kat-icon>`;
  }

  render() {
    return html`
      <div class="card">
        <div class="card-header">
          ${this.renderStateIcon()}
          <span class="title">${this.header}</span>
          <slot name="secondary-actions"></slot>
        </div>
        <slot></slot>
        <span class="card-date">${this.timestamp}</span>
        ${this.renderPrimaryActionSlot()}
      </div>
    `;
  }
}
